import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { GooglePlayStoreIcon, AppleStoreIcon } from 'assets/icons';
import { color, fontSize } from 'theme';
import { detectInAppWebView } from 'utils/browserDetection';

const StyledAppStoreWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  animation: appearance 1.5s ease-in;

  @keyframes appearance {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const StyledPromoText = styled.p`
  color: ${color.greyText};
  font-size: ${fontSize.small};
  margin: 0;
  text-align: center;
  line-height: 1.3;

  @media (min-width: 768px) {
    font-size: ${fontSize.regular};
  }
`;

const StyledSiteLink = styled.a`
  color: ${color.red};
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledStoreContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  justify-content: center;

  @media (min-width: 768px) {
    gap: 50px;
  }
`;

const StyledStoreLink = styled.a`
  display: block;
  width: 120px;
  height: 40px;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  & > svg {
    width: 100%;
    height: 100%;
  }

  @media (min-width: 768px) {
    width: 135px;
    height: 44px;
  }
`;

/**
 * Helper function to format URL to camel case based on site name
 * Matches improved Flutter implementation in main-box-info.dart
 * Properly handles domain hacks like titansti.me
 */
const formatUrlToCamelCase = (url, siteName) => {
  // Remove https:// or http:// if present
  const cleanUrl = url.replace(/^https?:\/\//, '');

  // Step 1: Create camel case mask from site name (remove spaces, capitalize each word)
  // Example: "Titans Time" -> "TitansTime"
  const words = siteName.split(' ');
  const camelCaseMask = words
    .map((word) => {
      if (!word) return '';
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');

  // Step 2: Normalize site name (remove spaces, lowercase)
  // Example: "Titans Time" -> "titanstime"
  const siteNameNormalized = siteName.replace(/\s+/g, '').toLowerCase();

  // Step 3: Find the first period in the URL
  const firstPeriodIndex = cleanUrl.indexOf('.');

  if (firstPeriodIndex === -1) {
    // No period, just return camel case mask
    return camelCaseMask;
  }

  // Step 4: Normalize the URL (remove ALL periods, lowercase)
  // Example: "titansti.me" -> "titanstime", "hawkstime.com" -> "hawkstimecom"
  const urlNormalized = cleanUrl.replace(/\./g, '').toLowerCase();

  // Step 5: Check if normalized URL STARTS WITH normalized site name
  // This handles both "titanstime" == "titanstime" and "hawkstimecom" starts with "hawkstime"
  if (urlNormalized.startsWith(siteNameNormalized)) {
    // Step 6: Check if the site name length matches the first period position
    // This tells us if the period is part of the domain hack or just the TLD separator
    if (firstPeriodIndex === siteNameNormalized.length) {
      // Simple case: period is the TLD separator
      // Example: "hawkstime.com" -> "HawksTime.com"
      return camelCaseMask + cleanUrl.substring(firstPeriodIndex);
    } else if (firstPeriodIndex < siteNameNormalized.length) {
      // Complex case: period is in the middle of the domain name
      // Example: "titansti.me" -> "TitansTi.me"
      return (
        camelCaseMask.substring(0, firstPeriodIndex) +
        cleanUrl.substring(firstPeriodIndex)
      );
    } else {
      // Period is after the site name length, just use camel case + rest
      return camelCaseMask + cleanUrl.substring(firstPeriodIndex);
    }
  }

  // Fallback: just capitalize first letter
  if (cleanUrl.length > 0) {
    return cleanUrl.charAt(0).toUpperCase() + cleanUrl.substring(1);
  }

  return cleanUrl;
};

/**
 * Helper function to construct app store URLs from store IDs
 */
const getStoreUrls = (appData) => {
  const iosStoreUrl = appData?.app_ios_store_id
    ? `https://apps.apple.com/app/id${appData.app_ios_store_id}`
    : null;
  const androidStoreUrl = appData?.app_android_store_id
    ? `https://play.google.com/store/apps/details?id=${appData.app_android_store_id}`
    : null;

  return { iosStoreUrl, androidStoreUrl };
};

export const AppStoreLinks = () => {
  const { error: isConfigError, data: configData } = useSelector(
    (state) => state.configuration
  );
  const { data: teamAppData } = useSelector((state) => state.teamApp);

  if (isConfigError) {
    return null;
  }

  // Check if this is a one-team site
  const isOneTeamSite =
    configData?.display_one_team === 1 || configData?.display_one_team === '1';

  // Priority: Team-level app data overrides site-level config
  // BUT if no team is selected, fall back to site-level config for multi-team sites
  const appData = teamAppData || configData;

  // Check if apps are enabled (team level or site level)
  const isIosEnabled = appData?.app_ios_enabled === 1;
  const isAndroidEnabled = appData?.app_android_enabled === 1;

  // Get store URLs - either from direct URL fields or construct from store IDs
  let iosStoreUrl = appData?.app_ios_store_url;
  let androidStoreUrl = appData?.app_android_store_url;

  const { isInAppWebView } = detectInAppWebView();

  // If using team app data (which has store IDs instead of URLs), construct URLs
  if (teamAppData && !iosStoreUrl && !androidStoreUrl) {
    const urls = getStoreUrls(teamAppData);
    iosStoreUrl = urls.iosStoreUrl;
    androidStoreUrl = urls.androidStoreUrl;
  }

  // For multi-team sites without team selected, use site-level app URLs or construct from IDs
  if (!teamAppData && !isOneTeamSite) {
    iosStoreUrl =
      configData?.app_ios_store_url ||
      (configData?.app_ios_store_id
        ? `https://apps.apple.com/app/id${configData.app_ios_store_id}`
        : null);
    androidStoreUrl =
      configData?.app_android_store_url ||
      (configData?.app_android_store_id
        ? `https://play.google.com/store/apps/details?id=${configData.app_android_store_id}`
        : null);
  }

  const hasIosUrl = !!iosStoreUrl;
  const hasAndroidUrl = !!androidStoreUrl;

  // Show iOS link only if enabled AND has URL
  const showIosLink = isIosEnabled && hasIosUrl;
  // Show Android link only if enabled AND has URL
  const showAndroidLink = isAndroidEnabled && hasAndroidUrl;
  // Show app store buttons if we have at least one app
  const hasAnyApp = showIosLink || showAndroidLink;

  // Get team name, app names, site name, and site URL from team app data
  const teamName = teamAppData?.team_name || appData?.one_team_name || '';
  const iosAppName =
    teamAppData?.app_ios_name || configData?.app_ios_name || '';
  const androidAppName =
    teamAppData?.app_android_name || configData?.app_android_name || '';
  const siteUrl = teamAppData?.site_url || '';
  const siteName = teamAppData?.site_name || appData?.site_name || '';
  const recommendedSiteURL = 'Downkount.com';
  const recommendedSiteName = 'Downkount';

  // For one-team sites: show if we have any app
  // For multi-team sites: show if we have team data and (apps or site URL)
  // OR if no team selected but site has apps enabled (Madness Time case) - caveat of showing recommended site if no team selected
  const shouldShowContent = isOneTeamSite
    ? hasAnyApp
    : teamName
      ? hasAnyApp || siteUrl
      : hasAnyApp; // Show site-level apps when no team selected

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {shouldShowContent ? (
        <StyledAppStoreWrapper>
          {!isInAppWebView &&
          ((isOneTeamSite && hasAnyApp) || (!isOneTeamSite && hasAnyApp)) ? (
            // Show app store buttons (one or both)
            <>
              {/* App store buttons */}
              <StyledStoreContainer>
                {showIosLink ? (
                  <StyledStoreLink
                    href={iosStoreUrl}
                    rel="noreferrer noopener"
                    target="_blank"
                    title={
                      iosAppName
                        ? `Open ${iosAppName}`
                        : 'Download on App Store'
                    }
                  >
                    <AppleStoreIcon />
                  </StyledStoreLink>
                ) : null}
                {showAndroidLink ? (
                  <StyledStoreLink
                    href={androidStoreUrl}
                    rel="noreferrer noopener"
                    target="_blank"
                    title={
                      androidAppName
                        ? `Open ${androidAppName}`
                        : 'Get it on Google Play'
                    }
                  >
                    <GooglePlayStoreIcon />
                  </StyledStoreLink>
                ) : null}
              </StyledStoreContainer>
              {!isOneTeamSite ? (
                <StyledPromoText>
                  Download {siteName} on your mobile device
                </StyledPromoText>
              ) : null}
            </>
          ) : !isOneTeamSite && siteUrl ? (
            <StyledPromoText>
              {teamName} fan? &nbsp;
              <StyledSiteLink
                href={`https://${siteUrl}`}
                rel="noreferrer noopener"
                target="_blank"
                title={`${teamName} fan? Visit ${siteName} | ${formatUrlToCamelCase(siteUrl, siteName)}`}
              >
                Visit {siteName}
              </StyledSiteLink>
              &nbsp; | &nbsp;{formatUrlToCamelCase(siteUrl, siteName)}
            </StyledPromoText>
          ) : isInAppWebView && recommendedSiteURL != siteUrl ? (
            <StyledPromoText>
              Like Madness Time? &nbsp;
              <StyledSiteLink
                href={`https://${recommendedSiteURL}`}
                onClick={(e) => {
                  e.preventDefault();
                  window.open(
                    `https://${recommendedSiteURL}`,
                    '_blank',
                    'noopener,noreferrer'
                  );
                }}
                rel="noreferrer noopener"
                target="_blank"
                title={`Like ${siteName}? Try ${recommendedSiteName}`}
              >
                Visit {recommendedSiteURL}
              </StyledSiteLink>
              &nbsp; | &nbsp; Downkount
            </StyledPromoText>
          ) : null}
        </StyledAppStoreWrapper>
      ) : null}
    </>
  );
};
